package com.schoolconnect.app.feature.schoolconfig

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.Switch
import androidx.compose.runtime.Composable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import kotlinx.coroutines.yield
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerType
import com.schoolconnect.app.designsystem.AppButton
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFieldHeight
import com.schoolconnect.app.designsystem.AppDialogFieldRow
import com.schoolconnect.app.designsystem.AppDialogHelperSize
import com.schoolconnect.app.designsystem.AppDialogInputSize
import com.schoolconnect.app.designsystem.AppDialogLabelSize
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSectionLabel
import com.schoolconnect.app.designsystem.AppDialogSectionTitleSize
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogColors
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppText
import com.schoolconnect.app.designsystem.AppDialogButtonTextSize
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.trash
import org.jetbrains.compose.resources.painterResource

private enum class EditTab { General, Calendar, Features }
private val SchoolConfigDialogColors = AppDialogColors(
    primary = AppPrimary,
    inputBorder = AppPrimary,
    navy = AppNavy
)

@Composable
internal fun SchoolConfigEditDialog(
    row: SchoolConfigRow,
    config: SchoolConfigEditData?,
    isSaving: Boolean,
    onDismiss: () -> Unit,
    onSave: (SchoolConfigEditData) -> Unit,
    onAcademicYearSelected: (AcademicYearOption) -> Unit,
    onDownloadHolidayTemplate: () -> Unit,
    onImportHolidays: (String, ByteArray) -> Unit,
    onOperationFailed: (String) -> Unit
) {
    val isLoadingConfig = config == null
    val dialogScope = rememberCoroutineScope()
    val dismissAfterClick: () -> Unit = { dialogScope.launch { yield(); onDismiss() }; Unit }
    val holidayPicker = rememberFilePickerLauncher(type = PickerType.File(listOf("xlsx")), title = "Select Holiday Workbook") { file ->
        if (file != null) dialogScope.launch {
            runCatching { file.readBytes() }
                .onSuccess { bytes -> onImportHolidays(file.name, bytes) }
                .onFailure { error -> onOperationFailed(error.message.orEmpty().ifBlank { "Unable to read the selected holiday workbook." }) }
        }
    }
    var tab by remember(row.id) { mutableStateOf(EditTab.General) }
    var academicYear by remember(row.id, config?.academicYearId, config?.academicYear) {
        mutableStateOf(config?.academicYear.orEmpty())
    }
    var weeklyOffDay by remember(row.id, config?.weeklyOffDay) { mutableStateOf(config?.weeklyOffDay ?: "Sunday") }
    var academicYearExpanded by remember(row.id) { mutableStateOf(false) }
    var offDayExpanded by remember(row.id) { mutableStateOf(false) }
    var holidays by remember(row.id, config) { mutableStateOf(config?.holidays.orEmpty()) }
    var attendance by remember(row.id, config) { mutableStateOf(config?.attendance.orEmpty()) }
    var exams by remember(row.id, config) { mutableStateOf(config?.exams.orEmpty()) }
    var features by remember(row.id, config) { mutableStateOf(config?.features.orEmpty()) }
    var attendanceError by remember(row.id, config?.academicYearId) { mutableStateOf("") }
    var examError by remember(row.id, config?.academicYearId) { mutableStateOf("") }

    fun clearThresholdErrors() {
        attendanceError = ""
        examError = ""
    }

    AppDialogScaffold(
        onDismiss = { if (!isSaving) dismissAfterClick() },
        colors = SchoolConfigDialogColors,
        size = AppDialogSize.Wide,
        dialogWidthOverride = 1120.dp,
        maxDialogHeight = 920.dp,
        fillDialogHeight = true,
        isLoading = isSaving || isLoadingConfig,
        loadingTitle = if (isSaving) "Saving configuration" else "Loading configuration"
    ) {
        AppDialogHeader(
            title = "Edit School Configuration",
            subtitle = "Configure settings for ${row.schoolName}.",
            onClose = { if (!isSaving) dismissAfterClick() },
            height = 90.dp,
            colors = SchoolConfigDialogColors
        )
        AppDialogDivider(colors = SchoolConfigDialogColors)
        AppDialogBody(
            horizontalPadding = 24.dp,
            verticalPadding = 12.dp,
            colors = SchoolConfigDialogColors,
            fillAvailable = true,
            showVerticalScrollbar = true
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                AppDialogFieldRow(spacing = 16.dp) {
                    AppDialogFieldColumn(
                        label = "School Name",
                        modifier = Modifier.weight(1f),
                        colors = SchoolConfigDialogColors
                    ) {
                        AppDialogTextField(
                            value = row.schoolName,
                            onValueChange = {},
                            placeholder = "School name",
                            modifier = Modifier.fillMaxWidth(),
                            readOnly = true,
                            colors = SchoolConfigDialogColors
                        )
                    }
                    AppDialogFieldColumn(
                        label = "Academic Year",
                        required = true,
                        modifier = Modifier.weight(1f),
                        colors = SchoolConfigDialogColors
                    ) {
                        AppDialogSelectField(
                            value = academicYear,
                            placeholder = "Select academic year",
                            expanded = academicYearExpanded,
                            options = config?.academicYears.orEmpty().map { it.label },
                            onToggleExpanded = { if (!isLoadingConfig) academicYearExpanded = !academicYearExpanded },
                            onDismissExpanded = { academicYearExpanded = false },
                            onSelected = { selected ->
                                academicYear = selected
                                academicYearExpanded = false
                                clearThresholdErrors()
                                config?.academicYears?.firstOrNull { it.label == selected }?.let(onAcademicYearSelected)
                            },
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
                // Keep tabs + underline divider in one block so body spacing does not separate them.
                Column(modifier = Modifier.fillMaxWidth()) {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        EditTabButton("General", tab == EditTab.General) { tab = EditTab.General }
                        EditTabButton("Calendar", tab == EditTab.Calendar) { tab = EditTab.Calendar }
                        EditTabButton("Features", tab == EditTab.Features) { tab = EditTab.Features }
                    }
                    HorizontalDivider(color = SchoolConfigDialogColors.border)
                }
                when (tab) {
                    EditTab.General -> GeneralConfigContent(
                        attendance = attendance,
                        exams = exams,
                        attendanceError = attendanceError,
                        examError = examError,
                        onAttendanceChange = { index, value ->
                            clearThresholdErrors()
                            attendance = attendance.toMutableList().also { it[index] = value }
                        },
                        onExamChange = { index, value ->
                            clearThresholdErrors()
                            exams = exams.toMutableList().also { it[index] = value }
                        },
                        onAttendanceAdd = {
                            clearThresholdErrors()
                            attendance = attendance + ThresholdDraft("", "0", "0")
                        },
                        onAttendanceRemove = { index ->
                            clearThresholdErrors()
                            attendance = attendance.filterIndexed { i, _ -> i != index }
                        },
                        onExamAdd = {
                            clearThresholdErrors()
                            exams = exams + ThresholdDraft("", "0", "0", "")
                        },
                        onExamRemove = { index ->
                            clearThresholdErrors()
                            exams = exams.filterIndexed { i, _ -> i != index }
                        }
                    )
                    EditTab.Calendar -> CalendarConfigContent(weeklyOffDay, offDayExpanded, holidays,
                        onToggleOffDay = { offDayExpanded = !offDayExpanded }, onDismissOffDay = { offDayExpanded = false },
                        onOffDaySelected = { weeklyOffDay = it; offDayExpanded = false },
                        onHolidayChange = { index, value -> holidays = holidays.toMutableList().also { it[index] = value } },
                        onAdd = { holidays = holidays + HolidayDraft("", "") },
                        onRemove = { index -> holidays = holidays.filterIndexed { i, _ -> i != index } },
                        onDownloadTemplate = onDownloadHolidayTemplate,
                        onImport = holidayPicker::launch)
                    EditTab.Features -> FeaturesConfigContent(
                        features = features,
                        onChange = { index, value -> features = features.toMutableList().also { it[index] = value } },
                        onAdd = { features = features + FeatureDraft("", true) },
                        onRemove = { index -> features = features.filterIndexed { i, _ -> i != index } }
                    )
                }
            }
        }
        AppDialogDivider(colors = SchoolConfigDialogColors)
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction(
                    "Cancel",
                    dismissAfterClick,
                    AppDialogActionStyle.Secondary,
                    enabled = !isSaving,
                    width = 120.dp
                ),
                AppDialogFooterAction(
                    if (isSaving) "Saving..." else "Save",
                    {
                        val loaded = config
                        if (loaded != null) {
                            val errors = validateSchoolConfigThresholds(attendance, exams)
                            attendanceError = errors.attendance
                            examError = errors.exams
                            if (errors.hasAny) {
                                tab = EditTab.General
                            } else {
                                onSave(
                                    SchoolConfigEditData(
                                        academicYear = academicYear,
                                        academicYearId = loaded.academicYears.firstOrNull { it.label == academicYear }?.id
                                            ?: loaded.academicYearId,
                                        academicYears = loaded.academicYears,
                                        weeklyOffDay = weeklyOffDay,
                                        attendance = attendance,
                                        exams = exams,
                                        holidays = holidays,
                                        features = features,
                                        weeklyHolidayApiAvailable = loaded.weeklyHolidayApiAvailable
                                    )
                                )
                            }
                        }
                    },
                    AppDialogActionStyle.Primary,
                    enabled = !isSaving && !isLoadingConfig,
                    width = 120.dp
                )
            ),
            colors = SchoolConfigDialogColors,
            contentPadding = PaddingValues(horizontal = 24.dp)
        )
    }
}

@Composable private fun EditTabButton(label: String, selected: Boolean, onClick: () -> Unit) {
    val interactionSource = remember { MutableInteractionSource() }
    Column(
        Modifier.width(108.dp).clickable(
            interactionSource = interactionSource,
            indication = null,
            onClick = onClick
        ),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            label,
            color = if (selected) AppPrimary else AppNavy,
            fontSize = AppDialogButtonTextSize,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
            modifier = Modifier.padding(top = 10.dp, bottom = 8.dp)
        )
        Box(Modifier.fillMaxWidth().height(2.dp).background(if (selected) AppPrimary else Color.Transparent))
    }
}

@Composable private fun GeneralConfigContent(
    attendance: List<ThresholdDraft>,
    exams: List<ThresholdDraft>,
    attendanceError: String,
    examError: String,
    onAttendanceChange: (Int, ThresholdDraft) -> Unit,
    onExamChange: (Int, ThresholdDraft) -> Unit,
    onAttendanceAdd: () -> Unit,
    onAttendanceRemove: (Int) -> Unit,
    onExamAdd: () -> Unit,
    onExamRemove: (Int) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        AppText("Configure attendance and examination performance thresholds.", color = AppNavy)
        if (attendance.isEmpty() && exams.isEmpty()) {
            AppText(
                "No threshold configuration was returned for this school and academic year. Add rows below or ask the backend team to seed the configuration.",
                color = AppMuted
            )
        }
        ThresholdSection(
            title = "Attendance Performance Thresholds",
            subtitle = "Define attendance levels for each percentage range. Ranges must cover 0%–100% with no gaps or overlaps.",
            rows = attendance,
            hasGrade = false,
            errorMessage = attendanceError,
            onChange = onAttendanceChange,
            onAdd = onAttendanceAdd,
            onRemove = onAttendanceRemove
        )
        ThresholdSection(
            title = "Exam Performance & Grade Thresholds",
            subtitle = "Define the performance level and grade assigned to each marks range. Ranges must cover 0%–100% with no gaps or overlaps.",
            rows = exams,
            hasGrade = true,
            errorMessage = examError,
            onChange = onExamChange,
            onAdd = onExamAdd,
            onRemove = onExamRemove
        )
    }
}

@Composable private fun ThresholdSection(
    title: String,
    subtitle: String,
    rows: List<ThresholdDraft>,
    hasGrade: Boolean,
    errorMessage: String,
    onChange: (Int, ThresholdDraft) -> Unit,
    onAdd: () -> Unit,
    onRemove: (Int) -> Unit
) {
    Column(Modifier.fillMaxWidth().border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(10.dp)).padding(10.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        AppDialogSectionLabel(title, fontSize = AppDialogSectionTitleSize)
        Text(subtitle, color = AppMuted, fontSize = AppDialogHelperSize, fontWeight = FontWeight.Medium)
        HorizontalDivider(color = AppDialogDefaultColors.border)
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                if (hasGrade) "Performance Level" else "Attendance Level",
                color = AppNavy,
                fontSize = AppDialogLabelSize,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.weight(1.3f)
            )
            if (hasGrade) {
                Text("Grade", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(.7f))
            }
            Text("Minimum %", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
            Text("Maximum %", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
            Text("Action", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.width(48.dp))
        }
        rows.forEachIndexed { index, row ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                AppDialogTextField(row.level, { onChange(index, row.copy(level = it)) }, "Enter level", Modifier.weight(1.3f), fieldHeight = AppDialogFieldHeight, fontSize = AppDialogInputSize)
                if (hasGrade) AppDialogTextField(row.grade, { onChange(index, row.copy(grade = it)) }, "Grade", Modifier.weight(.7f), fieldHeight = AppDialogFieldHeight, fontSize = AppDialogInputSize)
                AppDialogTextField(row.minimum, { onChange(index, row.copy(minimum = it)) }, "0", Modifier.weight(1f), fieldHeight = AppDialogFieldHeight, fontSize = AppDialogInputSize)
                AppDialogTextField(row.maximum, { onChange(index, row.copy(maximum = it)) }, "0", Modifier.weight(1f), fieldHeight = AppDialogFieldHeight, fontSize = AppDialogInputSize)
                Box(modifier = Modifier.width(48.dp), contentAlignment = Alignment.Center) {
                    Icon(
                        painterResource(Res.drawable.trash),
                        "Remove row",
                        tint = AppNavy,
                        modifier = Modifier.size(20.dp).clickable { onRemove(index) }
                    )
                }
            }
        }
        AppOutlinedButton(
            onClick = onAdd,
            modifier = Modifier.width(132.dp).height(AppDialogFieldHeight),
            colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary)
        ) { Text("+  Add Row", fontSize = AppDialogButtonTextSize, fontWeight = FontWeight.Medium) }
        if (errorMessage.isNotBlank()) {
            Text(errorMessage, color = AppDanger, fontSize = AppDialogHelperSize, fontWeight = FontWeight.Medium)
        }
    }
}

@Composable private fun CalendarConfigContent(
    weeklyOffDay: String, expanded: Boolean, holidays: List<HolidayDraft>, onToggleOffDay: () -> Unit,
    onDismissOffDay: () -> Unit, onOffDaySelected: (String) -> Unit, onHolidayChange: (Int, HolidayDraft) -> Unit,
    onAdd: () -> Unit, onRemove: (Int) -> Unit, onDownloadTemplate: () -> Unit, onImport: () -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        AppText("Configure weekly off days and school holidays for the selected academic year.", color = AppNavy)
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(10.dp))
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            AppDialogSectionLabel("School Holidays", fontSize = AppDialogSectionTitleSize)
            Text(
                "Holidays apply to the entire school. Set the weekly off day and manage holiday dates below.",
                color = AppMuted,
                fontSize = AppDialogHelperSize,
                fontWeight = FontWeight.Medium
            )
            HorizontalDivider(color = AppDialogDefaultColors.border)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.Bottom
            ) {
                AppDialogFieldColumn(
                    label = "Weekly Off Days",
                    required = true,
                    colors = SchoolConfigDialogColors
                ) {
                    AppDialogSelectField(
                        weeklyOffDay,
                        "Select day",
                        expanded,
                        listOf("Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"),
                        onToggleOffDay,
                        onDismissOffDay,
                        onOffDaySelected,
                        Modifier.width(180.dp)
                    )
                }
                Box(modifier = Modifier.weight(1f))
                Row(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    AppOutlinedButton(
                        onClick = onDownloadTemplate,
                        modifier = Modifier.height(AppDialogFieldHeight),
                        colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary),
                        border = BorderStroke(1.dp, AppPrimary)
                    ) { Text("Download Template", fontSize = AppDialogButtonTextSize, fontWeight = FontWeight.Medium) }
                    AppOutlinedButton(
                        onClick = onImport,
                        modifier = Modifier.height(AppDialogFieldHeight),
                        colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary),
                        border = BorderStroke(1.dp, AppPrimary)
                    ) { Text("Import Holidays", fontSize = AppDialogButtonTextSize, fontWeight = FontWeight.Medium) }
                    AppButton(
                        onClick = onAdd,
                        modifier = Modifier.height(AppDialogFieldHeight),
                        colors = ButtonDefaults.buttonColors(containerColor = AppPrimary, contentColor = Color.White)
                    ) { Text("+  Add Row", fontSize = AppDialogButtonTextSize, fontWeight = FontWeight.Medium) }
                }
            }
            if (holidays.isEmpty()) {
                Text("No holidays added yet. Import a template or add a row.", color = AppMuted, fontSize = AppDialogHelperSize, fontWeight = FontWeight.Medium)
            } else {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("Date", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(.8f))
                    Text("Holiday Name", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1.5f))
                    Text("Action", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.width(55.dp))
                }
                holidays.forEachIndexed { index, holiday ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        AppDialogDateField(holiday.date, { onHolidayChange(index, holiday.copy(date = it)) }, Modifier.weight(.8f), fieldHeight = AppDialogFieldHeight, fontSize = AppDialogInputSize)
                        AppDialogTextField(holiday.name, { onHolidayChange(index, holiday.copy(name = it)) }, "Holiday name", Modifier.weight(1.5f), fieldHeight = AppDialogFieldHeight, fontSize = AppDialogInputSize)
                        Icon(
                            painterResource(Res.drawable.trash),
                            "Remove holiday",
                            tint = AppNavy,
                            modifier = Modifier.width(55.dp).size(20.dp).clickable { onRemove(index) }
                        )
                    }
                }
            }
        }
    }
}

@Composable private fun FeaturesConfigContent(
    features: List<FeatureDraft>,
    onChange: (Int, FeatureDraft) -> Unit,
    onAdd: () -> Unit,
    onRemove: (Int) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        AppText("Configure optional features enabled for this school.", color = AppNavy)
        features.forEachIndexed { index, feature ->
            Row(Modifier.fillMaxWidth().border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(8.dp)).padding(10.dp), horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.CenterVertically) {
                AppDialogTextField(feature.key, { onChange(index, feature.copy(key = it)) }, "Feature key", Modifier.weight(1f), fieldHeight = AppDialogFieldHeight, fontSize = AppDialogInputSize)
                Switch(checked = feature.enabled, onCheckedChange = { onChange(index, feature.copy(enabled = it)) })
                Icon(painterResource(Res.drawable.trash), "Remove feature", tint = AppNavy, modifier = Modifier.size(20.dp).clickable { onRemove(index) })
            }
        }
        if (features.isEmpty()) {
            Text("No feature configurations yet. Add one to initialize this section.", color = AppMuted, fontSize = AppDialogHelperSize, fontWeight = FontWeight.Medium)
        }
        AppOutlinedButton(
            onClick = onAdd,
            modifier = Modifier.width(170.dp).height(AppDialogFieldHeight),
            colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary),
            border = BorderStroke(1.dp, AppPrimary)
        ) { Text("+  Add Feature", fontSize = AppDialogButtonTextSize, fontWeight = FontWeight.Medium) }
    }
}
